/**
 * Top-level router for evse3.
 *
 * Resolves the URL intent against the Dexie registry:
 *   - registry / no-match            -> RegistryPage
 *   - openExistingStation (?station) -> StationPage seeded from saved snapshot
 *   - quickConfigByCpid + saved      -> StationPage seeded from saved snapshot
 *   - quickConfigByCpid + new        -> StationPage with ephemeral identity
 *                                       (persisted after first successful boot)
 *
 * Before mounting StationPage we acquire the CrossTabLock for (cpid, wsUrl).
 * If the lock is held by another tab in the same browser profile we render
 * StationLockedPage instead — see CLAUDE.md §4.4.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ConnectorId,
  EvseId,
  OcppVersion,
  type OcppVersion as OcppVersionT,
  type StationId,
} from '@/domain/types';
import type { StationSnapshot } from '@/domain';
import { newStationId } from '@/domain/newStationId';
import { useStationStore, type EvseState } from '@/state/useStationStore';
import { useTabLockStore } from '@/state/useTabLockStore';
import { useRecoveryStore } from '@/state/useRecoveryStore';
import { useUIStore } from '@/state/useUIStore';
import { parseBootIntent, type BootIntent } from '@/url/bootIntent';
import { StationPage } from '@/ui/pages/StationPage';
import { RegistryPage } from '@/ui/pages/RegistryPage';
import { StationLockedPage } from '@/ui/pages/StationLockedPage';
import { VehiclesPage } from '@/ui/pages/VehiclesPage';
import { AppFooter } from '@/ui/components/AppFooter';
import { Logo } from '@/ui/components/Logo';
import { StationRepositoryDexie, VehicleRepositoryDexie } from '@/persistence/dexie/repositories';
import { db } from '@/persistence/dexie/db';
import { shouldRedirectToWelcome } from '@/state/welcomeRedirect';
import { useVehicleStore } from '@/state/useVehicleStore';
import { seedVehiclePresetsIfEmpty } from '@/state/vehicleProfilePresets';

/**
 * Session 6/7 default topology: one AC EVSE (22 kW) + one DC EVSE (50 kW,
 * dual-head). Matches the evse2 reference so QA muscle memory carries over.
 */
const DEFAULT_TOPOLOGY: { cpMaxPowerW: number; evses: readonly EvseState[] } = {
  cpMaxPowerW: 100_000,
  evses: [
    {
      id: EvseId.of(1),
      name: 'EVSE 1 (AC)',
      kind: 'AC',
      maxPowerW: 22_000,
      connectorIds: [ConnectorId.of(1)],
    },
    {
      id: EvseId.of(2),
      name: 'EVSE 2 (DC)',
      kind: 'DC',
      maxPowerW: 50_000,
      connectorIds: [ConnectorId.of(2), ConnectorId.of(3)],
    },
  ],
};

interface ResolvedStation {
  readonly stationId: StationId;
  readonly cpid: string;
  readonly wsUrl: string;
  readonly ocppVersion: OcppVersionT;
  /**
   * Topology from the saved station record, when one exists. Falls back
   * to DEFAULT_TOPOLOGY for ephemeral identities. Surviving a topology
   * edit + reload depends on plumbing this through (FEAT-002).
   */
  readonly topology?: { cpMaxPowerW: number; evses: readonly EvseState[] };
}

type Route =
  | { kind: 'registry' }
  | { kind: 'vehicles' }
  | { kind: 'resolving' }
  | { kind: 'station'; station: ResolvedStation }
  | { kind: 'locked'; station: ResolvedStation };

export default function App(): JSX.Element {
  const initStation = useStationStore((s) => s.init);
  const tryAcquire = useTabLockStore((s) => s.tryAcquire);
  const scanRecovery = useRecoveryStore((s) => s.scan);
  const setAuthorizeInput = useUIStore((s) => s.setAuthorizeInput);
  const [route, setRoute] = useState<Route>({ kind: 'resolving' });

  const intent = parseBootIntent(new URL(window.location.href));

  const resolve = useCallback(async (): Promise<void> => {
    if (intent.kind === 'registry') {
      // FEAT-016 — first-visit users get sent to the marketing landing at
      // /welcome instead of the empty registry page. The `from` URL guard
      // breaks the redirect loop when the landing's "Launch app" CTA sends
      // the user back here. We only redirect on a same-origin static-site
      // deploy where /welcome is served — best-effort on dev (FEAT-022's
      // middleware plugin) and production (FEAT-020's _redirects). If the
      // registry has any saved stations, fall through to RegistryPage.
      const redirect = await shouldRedirectToWelcome({
        href: window.location.href,
        repo: new StationRepositoryDexie(db),
      });
      if (redirect) {
        window.location.replace('/welcome');
        return;
      }
      setRoute({ kind: 'registry' });
      return;
    }
    if (intent.kind === 'vehicles') {
      setRoute({ kind: 'vehicles' });
      return;
    }
    if (intent.tag !== undefined) setAuthorizeInput(intent.tag);
    const resolved = await resolveStation(intent);
    if (!resolved) {
      setRoute({ kind: 'registry' });
      return;
    }
    initStation({
      stationId: resolved.stationId,
      cpid: resolved.cpid,
      wsUrl: resolved.wsUrl,
      ocppVersion: resolved.ocppVersion,
      topology: resolved.topology ?? DEFAULT_TOPOLOGY,
    });
    const { acquired } = await tryAcquire(resolved.cpid, resolved.wsUrl);
    if (!acquired) {
      setRoute({ kind: 'locked', station: resolved });
      return;
    }
    void scanRecovery(resolved.stationId);
    setRoute({ kind: 'station', station: resolved });
  }, [intent.kind, intent.tag, initStation, scanRecovery, setAuthorizeInput, tryAcquire]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void resolve();
  }, [resolve]);

  // BUG-010 — Seed vehicle presets + hydrate the workspace-wide vehicle
  // store ONCE at app boot. Previously this lived only in VehiclesPage's
  // useEffect, so any user who opened a station directly (most common
  // flow) saw an empty vehicle dropdown in ConnectorRow. Boot-time
  // seeding ensures the dropdown is populated on first render of every
  // route.
  useEffect(() => {
    void (async (): Promise<void> => {
      const repo = new VehicleRepositoryDexie(db);
      try {
        await seedVehiclePresetsIfEmpty(repo);
        await useVehicleStore.getState().loadAll();
      } catch (err) {
        // Vehicle library failure must not block the app from booting
        // — without presets the dropdown is empty but the rest works.
        console.warn('[evse3] vehicle store init failed', err);
      }
    })();
  }, []);

  useEffect(() => {
    // Best-effort release on tab close/navigation. We listen to BOTH events:
    //   - `pagehide` fires reliably on modern browsers including mobile Safari
    //     and bfcache transitions, where `beforeunload` may not fire.
    //   - `beforeunload` is kept as a safety net for older paths.
    // Refresh-survival is handled by the stable tabId + self-takeover in
    // CrossTabLock, so it's OK if neither handler completes in time; the
    // reaper still cleans up within ~10 s.
    const release = (): void => {
      void useTabLockStore.getState().release();
    };
    window.addEventListener('pagehide', release);
    window.addEventListener('beforeunload', release);
    return () => {
      window.removeEventListener('pagehide', release);
      window.removeEventListener('beforeunload', release);
    };
  }, []);

  // Wrap every route in a flex column so AppFooter sticks to the bottom.
  let body: JSX.Element;
  if (route.kind === 'resolving') {
    body = (
      <main
        className="flex min-h-full flex-1 flex-col items-center justify-center bg-slate-950 text-sm text-slate-400"
        data-testid="resolving"
      >
        <Logo variant="mark" size="lg" testId="resolving" />
        <p className="mt-3">Resolving station…</p>
      </main>
    );
  } else if (route.kind === 'registry') {
    body = <RegistryPage />;
  } else if (route.kind === 'vehicles') {
    body = <VehiclesPage />;
  } else if (route.kind === 'locked') {
    body = (
      <StationLockedPage
        onRetry={() => {
          void resolve();
        }}
      />
    );
  } else {
    body = <StationPage />;
  }
  return (
    <div className="flex min-h-full flex-col">
      {body}
      <AppFooter />
    </div>
  );
}

async function resolveStation(intent: BootIntent): Promise<ResolvedStation | null> {
  if (intent.kind === 'registry' || intent.kind === 'vehicles') return null;
  const repo = new StationRepositoryDexie(db);

  if (intent.kind === 'openExistingStation') {
    const snap = await repo.get(intent.stationId);
    if (!snap) return null;
    return toResolved(snap);
  }

  // quickConfigByCpid
  if (intent.wsUrl !== null) {
    const saved = await repo.findByIdentity(intent.cpid, intent.wsUrl);
    if (saved) return toResolved(saved);
    // New ephemeral identity — persisted on first successful boot.
    return {
      stationId: newStationId(),
      cpid: intent.cpid,
      wsUrl: intent.wsUrl,
      ocppVersion: intent.ocppVersion,
    };
  }
  // cpid-only: pick the unique saved match (if any). Multi-match picker UI
  // is on the carryover list for Session 8.
  const matches = (await repo.list()).filter((s) => s.cpid === intent.cpid);
  const onlyMatch = matches.length === 1 ? matches[0] : undefined;
  if (onlyMatch) return toResolved(onlyMatch);
  return null;
}

function toResolved(snap: StationSnapshot): ResolvedStation {
  return {
    stationId: snap.id,
    cpid: snap.cpid,
    wsUrl: snap.wsUrl,
    ocppVersion: snap.ocppVersion,
    topology: {
      cpMaxPowerW: snap.topology.cpMaxPowerW,
      evses: snap.topology.evses.map((e) => ({
        id: e.id,
        name: e.name,
        maxPowerW: e.maxPowerW,
        connectorIds: e.connectorIds,
      })),
    },
  };
}

// Keep an explicit re-export of OcppVersion so any consumer that imports it
// transitively from this entry doesn't lose access during code-splitting.
export { OcppVersion };
